import * as React from 'react'
import { DiagramEngine, PortWidget } from '@projectstorm/react-diagrams-core'
import { ConnectedApplicationsNodeModel } from './connected-applications-node-model'
import { css, cx } from '@emotion/css'
import {
  ApplicationPreviewCard,
  containerHeight
} from '../../application-preview-card'
import { List } from 'react-virtualized'
import { buildConnectedAppPortName } from './index'
import {
  connectedAppsColumnWidth,
  diagramHeight,
  getColumnContainerStyle
} from '../../index'
import { LegacyGridSpinner } from '../../../../legacy-design-system/legacy-grid'
import { FlowMapEmptyColumn } from '../../flow-map-empty-column'

const itemSeparatorHeight = 20
const itemHeight = containerHeight + itemSeparatorHeight

interface Props {
  readonly node: ConnectedApplicationsNodeModel
  readonly engine: DiagramEngine
}

export class ConnectedApplicationsNodeWidget extends React.Component<Props> {
  public render() {
    const { node, engine } = this.props

    const {
      items,
      organizationID,
      features,
      isFreemiumOrganization,
      onRowsRendered,
      isLoading
    } = node

    return (
      <div
        className={cx(
          getColumnContainerStyle(connectedAppsColumnWidth, 0),
          css`
            cursor: default;
            align-items: center;
          `
        )}
      >
        {isLoading ? (
          <LegacyGridSpinner data-testid="connected-applications-loading-indicator" />
        ) : !Array.isArray(items) || items.length === 0 ? (
          <div
            className={css`
              display: flex;
              flex-direction: column;
              align-items: center;
              padding-left: 25px;
            `}
          >
            <FlowMapEmptyColumn
              titleI18n={'title_applicationFlowMap_noApplications'}
              descriptionI18n={'detail_applicationFlowMap_noApplications'}
            />
          </div>
        ) : (
          <List
            className={css`
              outline: none;
            `}
            height={diagramHeight}
            width={connectedAppsColumnWidth}
            overscanRowCount={0}
            rowCount={items.length}
            rowHeight={itemHeight}
            rowRenderer={({ style, index: rowIndex, key, isScrolling }) => {
              if (isScrolling) {
                return <div style={style} key={key} />
              }

              const portName = buildConnectedAppPortName(rowIndex, 'in')
              const portIn = node.getPort(portName)

              const { application: app } = items[rowIndex]

              return (
                <div style={style} key={key}>
                  <div
                    className={css`
                      display: flex;
                      justify-content: flex-end;
                    `}
                  >
                    <div
                      className={css`
                        position: relative;
                        margin-bottom: ${itemSeparatorHeight}px;
                      `}
                    >
                      {!!portIn && (
                        <PortWidget
                          engine={engine}
                          port={portIn}
                          className={css`
                            position: absolute;
                            top: 50%;
                            left: 0;
                          `}
                        />
                      )}
                      <ApplicationPreviewCard
                        organizationID={organizationID}
                        applicationID={app.id}
                        applicationName={app.name}
                        url={app.url}
                        lastActivity={app.lastActivity}
                        online={app.online}
                        primary={app.primary}
                        assess={app.hasAssess}
                        hasAProtectedServer={app.hasAProtectedServer}
                        isFreemiumOrganization={isFreemiumOrganization}
                        moduleCount={app.moduleCount}
                        language={app.language}
                        violatedCompliancePolicies={
                          app.violatedCompliancePolicies
                        }
                        organizationRole={app.role}
                        vulnerabilityBreakdown={app.vulnerabilityBreakdown}
                        attackStatus={app.attackStatus}
                        features={features}
                        tooltipPlacement={rowIndex === 0 ? 'bottom' : undefined}
                      />
                    </div>
                  </div>
                </div>
              )
            }}
            onRowsRendered={({
              overscanStartIndex,
              overscanStopIndex,
              startIndex,
              stopIndex
            }) => {
              // eslint-disable-next-line no-console
              console.log(
                `onRowsRendered widget - connected apps: ${startIndex} - ${stopIndex} ${overscanStartIndex} - ${overscanStopIndex}`
              )

              onRowsRendered(startIndex, stopIndex, 'CONNECTED_APPS')
            }}
          />
        )}
      </div>
    )
  }
}
